import { groupIntoConversations } from "@m8t-stack/agent-ledger/read";
import type { LedgerConversation, LedgerRow } from "@m8t-stack/agent-ledger/read";
import { maskContactStrict } from "@m8t-stack/agent-ledger";
import {
  ConvFetchError, DEFAULT_SAFETY_LAG_SECONDS, ENGINE_VERSION, SOURCES, canonicalWorker,
} from "./types";
import type {
  LedgerSource, ConversationSource, TraceSource, Cursor, TeachingEpisodeSource,
  AdvancePlan, ConversationRow, DreamCursor, NormalizedConversation, NormalizedDreamInput, NormalizedItem, PkWatermark, RoundEnrichment,
} from "./types";
import { physicalPksFor } from "./ledger-source";
import { computeWatermark, rowStorageKey, justBefore } from "./cursor";
import { classifyGroup, SkipLedger, assertInvariant } from "./skip-ledger";
import { normalize } from "./normalize";
import type { NormalizeGroup } from "./normalize";
import {
  excludeTeachingItems,
  TeachingExclusionUnavailableError,
} from "./teaching-exclusion";

export interface PipelineSources {
  ledger: LedgerSource;
  conversations: ConversationSource;
  trace: TraceSource;
  cursor: Cursor;
  /** Explicit null means this installation has no durable TeachingEpisodes store. */
  teaching: TeachingEpisodeSource | null;
}

export interface RunPipelineArgs {
  worker: string;
  sources: PipelineSources;
  now: Date;              // injected clock — drives the PURE window/cursor math; never the wall clock
  dryRun: boolean;
  since?: string;         // ISO-8601 start override (validated at the CLI edge)
  reset?: boolean;        // ignore the stored cursor
  /**
   * Monotonic wall-clock for measuring stats.latencyMs around the awaited live
   * I/O. SEPARATE from `now` (which stays pure for window/cursor math); reading
   * the real clock HERE is fine — runPipeline is the I/O wrapper, not a pure
   * helper. Defaults to `Date.now`; tests inject a fake that advances.
   */
  monotonicNow?: () => number;
}

const isKnownSource = (s: string): boolean => (SOURCES as readonly string[]).includes(s);
export const MAX_CONV_FETCH_ATTEMPTS = 3;

/**
 * pure orchestration over injected sources. Every group lands in exactly
 * one terminal state; per-read failures are caught + counted, never fatal.
 * runPipeline is harvest-only: it COMPUTES an `AdvancePlan` (never past a
 * fetch-errored group) but NEVER commits it — critical. runDream commits the
 * plan via commitCursorAdvance only after a successful brain write, so a failed
 * write never burns the window.
 */
export async function runPipeline(args: RunPipelineArgs): Promise<NormalizedDreamInput> {
  const { worker, sources, now, since, reset } = args;
  // Wall-clock latency around the awaited live I/O — read from the REAL clock
  // (this is the I/O wrapper, not a pure helper), kept distinct from the injected
  // pure `now` so the window/cursor math stays deterministic.
  const monotonicNow = args.monotonicNow ?? Date.now;
  const t0 = monotonicNow();
  const target = canonicalWorker(worker);
  const physicalPks = physicalPksFor(worker);

  // 1. Cursor — tolerates a missing table (returns an absent cursor). --reset
  // ignores the stored cursor; --since overrides the start.
  let cursor = await sources.cursor.read(target);
  if (reset) {
    cursor = { worker: target, watermarks: {}, safetyLagSeconds: cursor.safetyLagSeconds || DEFAULT_SAFETY_LAG_SECONDS, engineVersion: ENGINE_VERSION };
  }
  const safetyLag = cursor.safetyLagSeconds || DEFAULT_SAFETY_LAG_SECONDS;
  if (since) {
    cursor = { ...cursor, watermarks: { ...cursor.watermarks } };
    for (const pk of physicalPks) {
      cursor.watermarks[pk] = { ts: since, consumedRowKeys: [] };
    }
  }

  // 2. Ledger rows since the watermark (PK-scoped, watermark+overlap inside rowsSince).
  const rows = await sources.ledger.rowsSince(physicalPks, cursor);

  // 3. Group into conversations (a2a -> delegationId; reuses the read helper).
  const groups: LedgerConversation[] = groupIntoConversations(rows);
  // Recover each group's actual LedgerRows (the cursor dedups on RowKey, which
  // LedgerRound does NOT carry — only LedgerRow does). Rebuild the SAME grouping
  // key groupIntoConversations uses so a consumed group maps back to its rows.
  const rowsByGroupKey = groupRows(rows);

  // 4–7. Classify-once, fetch/recover/skip, enrich, normalize.
  const skips = new SkipLedger();
  const conversations: NormalizedConversation[] = [];
  let consumed = 0;
  let recovered = 0;
  // The actual LedgerRows consumed (or content-recovered) this run, keyed by
  // physical PK — threaded into computeWatermark so the written watermark records
  // THIS run's RowKeys, enabling overlap-dedup on the next run.
  const consumedRowsByPk: Record<string, LedgerRow[]> = {};
  const failedTsByPk: Record<string, string> = {};      // earliest fetch-errored eventTimestamp per PK
  const newFailures: Record<string, number> = {};
  const window = windowFor(rows, now, since);

  for (const g of groups) {
    const pk = g.worker;                                 // worker IS the PartitionKey
    const isTarget = canonicalWorker(g.worker) === target && physicalPks.includes(g.worker.toLowerCase());
    const knownSource = isKnownSource(g.source);

    // Fast-reject classes that need no fetch (worker / attribution / source).
    if (!isTarget || !g.worker || !knownSource) {
      const outcome = classifyGroup({
        group: groupView(g),
        isTargetWorker: isTarget, hasConvId: !!g.conversationId,
        recovered: false, itemCount: 0, fetchError: undefined,
        knownSource, inTraceWindow: true,
      });
      if (outcome !== "consumed" && outcome !== "recovered") skips.addSkip(outcome, sampleId(g));
      continue;
    }

    let conversationId = g.conversationId;
    let items: NormalizedItem[] = [];
    let didRecover = false;
    let fetchError: ConvFetchError | undefined;
    let inTraceWindow = true;

    if (conversationId) {
      // conv-id path: fetch the transcript.
      ({ items, fetchError } = await fetchItems(sources.conversations, conversationId));
    } else if (g.detail.some((d) => d.responseId)) {
      // conv-less + responseId: attempt trace recovery within the window.
      const found = g.detail.find((d) => d.responseId);
      const respId = found?.responseId ?? "";
      let recoveredId: string | null = null;
      try {
        recoveredId = await sources.trace.recoverConversationId(respId, { from: window.from ?? "", to: window.to });
      } catch {
        recoveredId = null;
      }
      if (recoveredId) {
        conversationId = recoveredId;
        didRecover = true;
        ({ items, fetchError } = await fetchItems(sources.conversations, recoveredId));
        // HARD a2a gate: a recovered a2a conv is consolidated ONLY if
        // its own metadata.app proves it is m8t-a2a traffic (Option A, probe-
        // confirmed). An un-provable / wrong-app recovered conv is forced back to
        // a2a-unlinked (never half-trusted into memory). SCOPED to `source === 'a2a'`:
        // a non-a2a conv-less+responseId group (webapp/mcp/telegram) also reaches this
        // recovery branch (it groups as `__single__:<responseId>`), and its app tag is
        // legitimately NOT `m8t-a2a` — gating it here would silently drop real,
        // content-proven non-a2a recoveries (re-classified as no-conv-id and lost).
        if (g.source === "a2a" && !fetchError && items.length > 0 && sources.conversations.retrieveApp) {
          const app = await sources.conversations.retrieveApp(recoveredId);
          if (app !== "m8t-a2a") {
            didRecover = false;
            items = [];
            conversationId = undefined;
          }
        }
      } else {
        inTraceWindow = false; // no conv-id back from the trace => aged out (or no match)
      }
    }

    const outcome = classifyGroup({
      group: groupView(g),
      isTargetWorker: true,
      hasConvId: !!g.conversationId,
      recovered: didRecover,
      itemCount: items.length,
      fetchError: fetchError?.kind,
      knownSource,
      inTraceWindow,
    });

    if (outcome === "consumed" || outcome === "recovered") {
      const enrichment = await enrichGroup(sources.trace, g, window);
      // Canonicalize the worker (and the a2a target) so the seam-3 contract never
      // leaks a raw physical PK — a legacy pre-rename key, or merely a different
      // casing — into attribution.worker/target (the same `canonicalWorker` the
      // cursor + ledger-source agree on).
      const canonicalPk = canonicalWorker(g.worker);
      const ng: NormalizeGroup = {
        ...groupView(g),
        worker: canonicalPk,
        conversationId: conversationId ?? "",
        user: g.user,
        caller: g.initiatingAgent,
        target: g.target ? canonicalWorker(g.target) : undefined,
        delegationId: g.delegationId,
        startedAt: g.startedAt,
        lastAt: g.lastAt,
        rounds: g.rounds,
        outcome: g.outcome,
        recoveredVia: didRecover ? "trace" : undefined,
      };
      // Record THIS group's actual rows under its physical PK (RowKeys live on
      // LedgerRow). computeWatermark derives ts + consumedRowKeys from them.
      const grpRows = rowsByGroupKey.get(groupKeyOf(g)) ?? [];
      // Stamp the source-row provenance onto the conversation: the
      // SAME storage RowKeys (via the SAME rowStorageKey) + emit-times that feed
      // computeWatermark, so capCorpus can later narrow the advance to ONLY the
      // kept conversations' rows and re-derive a sound partial watermark.
      const provRows: ConversationRow[] = grpRows.map((r) => ({ rowKey: rowStorageKey(r), eventTimestamp: r.eventTimestamp }));
      // Contact details distill only for the install's own identified team.
      // Everything else fails closed with the strict transcript rules (bare
      // phone-length digit runs mask too — "your number?" → "0541234567"):
      // `svc:` refs are people the install SERVES (public visitors, relayed
      // customers); a2a rows carry the CALLER AGENT as user, so delegated
      // content may originate from a served end-user and cannot prove
      // otherwise; an absent ref resolves to "unknown" and proves nothing.
      // Secrets are already gone (conversation source).
      let teachingFilteredItems: NormalizedItem[];
      try {
        teachingFilteredItems = await excludeTeachingItems(
          sources.teaching,
          conversationId ?? "",
          items,
        );
      } catch (error) {
        if (!(error instanceof TeachingExclusionUnavailableError)) throw error;
        const id = conversationId ?? "";
        const next = (cursor.fetchFailures?.[id] ?? 0) + 1;
        newFailures[id] = next;
        skips.addSkip(
          "teaching-exclusion-unavailable",
          sampleId(g),
          next >= MAX_CONV_FETCH_ATTEMPTS
            ? `terminal after ${String(next)} cycles — conversation withheld; watermark released`
            : error.message,
        );
        // Persistently retry a bounded number of times. At the terminal count
        // the malformed conversation is still withheld, but stops poisoning
        // its whole PK so unrelated newer conversations can advance.
        if (next < MAX_CONV_FETCH_ATTEMPTS) {
          const currentFailure = failedTsByPk[pk];
          if (!currentFailure || g.startedAt < currentFailure) {
            failedTsByPk[pk] = g.startedAt;
          }
        }
        continue;
      }
      // Only a successful authoritative exclusion read may consume cursor
      // provenance. A failed lookup remains fenced for a later dream cycle.
      (consumedRowsByPk[pk] ??= []).push(...grpRows);
      const trustedOwner =
        g.source !== "a2a" && g.user.ref !== "unknown" && !g.user.ref.startsWith("svc:");
      const dreamItems = trustedOwner
        ? teachingFilteredItems
        : teachingFilteredItems.map((i) => ({ ...i, text: maskContactStrict(i.text) }));
      // A tool call's queries are the user's own words handed to a tool ("find
      // the invoice for dana@…"), so they carry whatever the transcript does and
      // take the SAME gate. They ride beside `items` rather than inside it, so
      // masking the transcript alone would have left them raw — inert today (no
      // dream consumer serializes them: the prompt renders items, the dry run
      // prints tool names), and exactly the kind of gap the next consumer would
      // assume was already closed. The `sources` beside them are
      // machine-produced identifiers, not user text, and are left as they are.
      const dreamEnrichment = trustedOwner || !enrichment
        ? enrichment
        : {
            ...enrichment,
            tools: enrichment.tools.map((t) => ({ ...t, queries: t.queries.map((q) => maskContactStrict(q)) })),
          };
      const conv = normalize(ng, dreamItems, dreamEnrichment);
      conv.provenance = { pk, rows: provRows };
      conversations.push(conv);
      if (outcome === "consumed") consumed += 1;
      else recovered += 1;
    } else {
      let lastError = fetchError?.message;
      if (outcome === "conv-fetch-failed") {
        const id = conversationId ?? "";
        const next = (cursor.fetchFailures?.[id] ?? 0) + 1;
        newFailures[id] = next;
        if (next >= MAX_CONV_FETCH_ATTEMPTS) {
          lastError = `terminal after ${String(next)} cycles — watermark released`;
        }
      }
      skips.addSkip(outcome, sampleId(g), lastError);
      // A fetch-errored OR auth-failed group must fence the watermark: record its
      // earliest ts so it is re-scanned next run (auth is invariant-safe).
      if (outcome === "auth-failed" || (outcome === "conv-fetch-failed" && conversationId && newFailures[conversationId] < MAX_CONV_FETCH_ATTEMPTS)) {
        const cur = failedTsByPk[pk];
        if (!cur || g.startedAt < cur) failedTsByPk[pk] = g.startedAt;
      }
    }
  }

  const skipLedger = skips.entries();
  const skipped = skips.total();
  const stats = {
    ledgerRows: rows.length,
    groups: groups.length,
    consumed,
    recovered,
    skipped,
    // Wall-clock elapsed around the awaited live I/O — measured from the
    // real/injected monotonic clock, NOT the pure `now` (which never moves within a run).
    latencyMs: Math.round(monotonicNow() - t0),
  };

  // Structural guarantee: nothing silently dropped.
  assertInvariant(stats, groups.length);

  // 8. COMPUTE the cursor advance — but DO NOT commit it here — critical.
  // runPipeline is harvest-only; runDream commits via commitCursorAdvance ONLY
  // after a successful brain write, so a failed write never burns the window.
  // Dry-run and non-dry compute the SAME plan; `dryRun` no longer changes behavior.
  const advance = buildAdvancePlan(target, cursor, physicalPks, consumedRowsByPk, failedTsByPk, newFailures, now, safetyLag);

  const out: NormalizedDreamInput = {
    worker: target,
    window,
    conversations,
    skipLedger,
    stats,
    advance,
  };

  return out;
}

// --- helpers --------------------------------------------------------------

function groupView(g: LedgerConversation) {
  return {
    conversationId: g.conversationId,
    source: g.source,
    worker: g.worker,
    responseId: g.detail.find((d) => d.responseId)?.responseId,
    delegationId: g.delegationId,
  };
}

const sampleId = (g: LedgerConversation): string =>
  g.conversationId ?? g.delegationId ?? g.detail[0]?.responseId ?? `${g.source}:${g.worker}`;

/**
 * The grouping key — VERBATIM mirror of groupIntoConversations' key so a raw
 * LedgerRow and the LedgerConversation it landed in resolve to the SAME bucket.
 * a2a → delegationId; else foundryConversationId; else a per-row singleton.
 */
function rowGroupKey(r: LedgerRow): string {
  return r.source === "a2a"
    ? `a2a:${r.delegationId ?? r.eventId}`
    : (r.foundryConversationId ?? `__single__:${r.responseId ?? r.eventId}`);
}

/** Bucket the raw rows by their grouping key so a consumed group recovers its
 *  actual LedgerRows (and thus their RowKeys, which LedgerRound lacks). */
function groupRows(rows: LedgerRow[]): Map<string, LedgerRow[]> {
  const by = new Map<string, LedgerRow[]>();
  for (const r of rows) {
    const k = rowGroupKey(r);
    const arr = by.get(k);
    if (arr) arr.push(r);
    else by.set(k, [r]);
  }
  return by;
}

/** The grouping key of an already-grouped LedgerConversation, mirroring
 *  rowGroupKey from the group's identity fields (delegationId / conversationId /
 *  representative responseId). */
function groupKeyOf(g: LedgerConversation): string {
  if (g.source === "a2a") return `a2a:${g.delegationId ?? sampleId(g)}`;
  if (g.conversationId) return g.conversationId;
  const respId = g.detail.find((d) => d.responseId)?.responseId;
  return `__single__:${respId ?? sampleId(g)}`;
}

async function fetchItems(
  src: ConversationSource,
  conversationId: string,
): Promise<{ items: NormalizedItem[]; fetchError?: ConvFetchError }> {
  try {
    const items = await src.items(conversationId);
    return { items };
  } catch (e) {
    if (e instanceof ConvFetchError) return { items: [], fetchError: e };
    // Any other throw is also a non-fatal fetch failure (never abort the run).
    return { items: [], fetchError: new ConvFetchError("terminal", e instanceof Error ? e.message : String(e)) };
  }
}

async function enrichGroup(
  trace: TraceSource,
  g: LedgerConversation,
  window: { from?: string; to: string },
): Promise<RoundEnrichment | undefined> {
  const respIds = g.detail.map((d) => d.responseId).filter((r): r is string => !!r);
  if (!respIds.length) return undefined;
  try {
    const map = await trace.enrich(respIds, { from: window.from ?? "", to: window.to });
    for (const r of respIds) {
      const hit = map.get(r);
      if (hit) return hit; // representative round; degrade to ledger-only if none
    }
  } catch {
    // enrichment failure degrades to ledger-only (acceptable).
  }
  return undefined;
}

function windowFor(rows: { eventTimestamp: string }[], now: Date, since?: string): { from?: string; to: string } {
  const to = now.toISOString();
  if (since) return { from: since, to };
  let from: string | undefined;
  for (const r of rows) if (!from || r.eventTimestamp < from) from = r.eventTimestamp;
  return { from, to };
}

/**
 * build the write-gated AdvancePlan. Same per-PK fence + monotonic re-pin
 * math as before, but RETURNS the plan instead of a DreamCursor: runDream commits
 * it post-write. consumedRowsByPk is collapsed to the storage RowKeys (the only
 * sound dedup key) so commitCursorAdvance reproduces computeWatermark exactly.
 */
function buildAdvancePlan(
  worker: string,
  cursor: DreamCursor,
  physicalPks: string[],
  consumedRowsByPk: Record<string, LedgerRow[]>,
  failedTsByPk: Record<string, string>,
  fetchFailures: Record<string, number>,
  now: Date,
  safetyLagSeconds: number,
): AdvancePlan {
  const watermarks: Record<string, PkWatermark> = {};
  const consumedKeysByPk: Record<string, string[]> = {};
  const priorTsByPk: Record<string, string> = {};
  for (const pk of physicalPks) {
    const rowsForPk = consumedRowsByPk[pk] ?? [];
    const { ts, consumedRowKeys } = computeWatermark(rowsForPk, now.toISOString(), safetyLagSeconds);
    let candidate = ts;
    const prior = Object.hasOwn(cursor.watermarks, pk) ? cursor.watermarks[pk].ts : undefined;
    if (prior) priorTsByPk[pk] = prior;
    if (prior && candidate < prior) candidate = prior;
    const failed = failedTsByPk[pk];
    if (failed && candidate >= failed) candidate = justBefore(failed);
    watermarks[pk] = { ts: candidate, consumedRowKeys };
    consumedKeysByPk[pk] = consumedRowKeys;
  }
  // Carry the watermark inputs so capCorpus re-derives a partial advance with the
  // SAME math (computeWatermark + re-pin + fence) — never by filtering the full ts.
  return {
    worker, watermarks, consumedRowsByPk: consumedKeysByPk, failedTsByPk,
    ...(Object.keys(fetchFailures).length > 0 ? { fetchFailures } : {}),
    now: now.toISOString(), safetyLagSeconds, priorTsByPk,
  };
}
